import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import LoginVerify from './pages/LoginVerify';
import Activate from './pages/Activate';
import ChangePassword from './pages/ChangePassword';
import AccountSettings from './pages/AccountSettings';
import Dashboard from './pages/Dashboard';
import ProjectDetail from './pages/ProjectDetail';
import SigningPackage from './pages/SigningPackage';
import Billing from './pages/Billing';
import Orders from './pages/Orders';
import Proposal from './pages/Proposal';
import RemediationPlan from './pages/RemediationPlan';
import ServiceProposals from './pages/ServiceProposals';
import Compliance from './pages/Compliance';
import Badge from './pages/Badge';
import Report from './pages/Report';
import ContractSigning from './pages/ContractSigning';
import AddPaymentMethod from './pages/AddPaymentMethod';
import PortalLayout from './components/PortalLayout';
import './App.css';

// Protected route wrapper
const ProtectedRoute = ({ children, bare = false }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="app-loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Every authenticated page gets the shared header/nav, so no page can be a
  // dead end. `bare` is for forced gates (set-your-password) where offering
  // navigation would let the user skip the gate.
  return bare ? children : <PortalLayout>{children}</PortalLayout>;
};

// Sticky banner shown whenever the active session was minted by staff via
// the "View as Client" feature. Lets the staffer get out cleanly without
// taking actions thinking they're in their own account.
const ImpersonationBanner = () => {
  const { user, logout } = useAuth();
  if (!user?.impersonated_by) return null;
  const exit = () => {
    logout();
    window.close();   // best-effort — close the impersonation tab
  };
  return (
    <div className="impersonation-banner">
      <span className="impersonation-banner-label">VIEWING AS CLIENT</span>
      <span className="impersonation-banner-detail">
        {user.email} · impersonated by <strong>{user.impersonated_by_name || 'staff'}</strong> · session expires in 2 hr
      </span>
      <button onClick={exit} className="impersonation-banner-exit">Exit View Mode</button>
    </div>
  );
};

// Public route wrapper (redirect if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="app-loading">Loading...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      {/* Magic-link landing — always processes the token (no PublicRoute
          wrapper) so it works even with a stale/expired session. */}
      <Route path="/login/verify" element={<LoginVerify />} />
      <Route path="/activate" element={<Activate />} />
      <Route path="/sign/:packageToken" element={<SigningPackage />} />

      {/* Protected routes */}
      <Route
        path="/change-password"
        element={
          <ProtectedRoute bare>
            <ChangePassword />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <ProjectDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        }
      />
      {/* Security Reports merged into the HIPAA page — keep the old path
          working for emailed links and bookmarks. */}
      <Route path="/reports" element={<Navigate to="/compliance?tab=reports" replace />} />
      <Route
        path="/service-proposals"
        element={
          <ProtectedRoute>
            <ServiceProposals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-proposals/:planId"
        element={
          <ProtectedRoute>
            <ServiceProposals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance"
        element={
          <ProtectedRoute>
            <Compliance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/badge"
        element={
          <ProtectedRoute>
            <Badge />
          </ProtectedRoute>
        }
      />
      <Route
        path="/report/:id"
        element={
          <ProtectedRoute>
            <Report />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposal/:pathId"
        element={
          <ProtectedRoute>
            <Proposal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/remediation-plan/:pathId"
        element={
          <ProtectedRoute>
            <RemediationPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contract/:pathId"
        element={
          <ProtectedRoute>
            <ContractSigning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/add-payment-method/:pathId"
        element={
          <ProtectedRoute>
            <AddPaymentMethod />
          </ProtectedRoute>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ImpersonationBanner />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
